import { Select, Tag } from 'antd';
import styles from './CountrySelect.module.sass';
import { getEmojiFlagByCode } from './CountrySelect.helpers';

const popupStyles = { popup: { root: { zIndex: 10000 } } } as const;

const DEFAULT_MAX_TAG_COUNT = 5;

type CountrySelectProps = {
  value?: string | string[];
  onChange?: (value: string | string[] | undefined) => void;
  countries: Record<string, string>;
  placeholder?: string;
  allowClear?: boolean;
  showSearch?: boolean;
  className?: string;
  status?: 'error' | 'warning';
  isMultiple?: boolean;
  maxTagCount?: number | 'responsive';
  disabled?: boolean;
};

export const CountrySelect = ({
  value,
  onChange,
  countries,
  placeholder,
  allowClear = true,
  showSearch = true,
  className,
  status,
  isMultiple = false,
  maxTagCount = DEFAULT_MAX_TAG_COUNT,
  disabled = false,
}: CountrySelectProps) => {
  const options = Object.entries(countries)
    .map(([code, name]) => ({
      value: code,
      label: `${getEmojiFlagByCode(code)} ${name}`,
      name,
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(({ value, label }) => ({ value, label }));

  const selectValue = isMultiple
    ? (value as string[] | undefined) ?? []
    : (value as string | undefined) || undefined;

  const effectiveMaxTagCount = isMultiple ? maxTagCount : undefined;

  const tagRender = ({
    value: tagValue,
    label,
    closable,
    onClose,
  }: {
    value: string;
    label: React.ReactNode;
    closable: boolean;
    onClose: () => void;
  }) => {
    const isOverflowPlaceholder =
      typeof label === 'string' && label.startsWith('+');
    if (isOverflowPlaceholder) {
      return <span className={styles.tagPlaceholder}>{label as string}</span>;
    }
    return (
      <Tag closable={closable} onClose={onClose}>
        {label}
      </Tag>
    );
  };

  return (
    <Select
      mode={isMultiple ? 'multiple' : undefined}
      value={selectValue}
      onChange={onChange}
      size="large"
      options={options}
      styles={popupStyles}
      placeholder={placeholder}
      allowClear={allowClear}
      showSearch={showSearch}
      className={className ?? styles.selectWhite}
      status={status}
      disabled={disabled}
      maxTagCount={effectiveMaxTagCount}
      maxTagPlaceholder={
        effectiveMaxTagCount
          ? (omittedValues) => `+${omittedValues.length}`
          : undefined
      }
      tagRender={isMultiple ? tagRender : undefined}
      filterOption={(input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
      }
      optionLabelProp="label"
    />
  );
};
